/**
 * 品牌管理主页面
 * 职责: 组装品牌管理的各个组件
 * 限制: <150行
 */

import React, { useEffect, useMemo } from 'react';
import { Table, Button, Drawer, Form, Row, Col, Space } from 'antd';
import {
  PlusOutlined,
  TagsOutlined,
  CheckCircleOutlined,
  StopOutlined,
  ShoppingOutlined,
} from '@ant-design/icons';
import { useBrandManagement } from './useBrandManagement';
import { createBrandTableColumns } from './BrandTableColumns';
import { BrandForm } from './BrandForm';
import { PageHeader } from '../../components/Layout/PageHeader';
import { StatsCard } from '../../components/UI/Card/StatsCard';
import { EmptyState } from '../../components/UI/EmptyState';

const BrandManagement: React.FC = () => {
  const [form] = Form.useForm();
  const {
    brands,
    loading,
    modalVisible,
    editingBrand,
    setModalVisible,
    loadBrands,
    handleAdd,
    handleEdit,
    handleDelete,
    handleToggleActive,
    handleSubmit,
  } = useBrandManagement();

  useEffect(() => {
    loadBrands();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 处理表单提交
  const onDrawerSubmit = async () => {
    try {
      const values = await form.validateFields();
      const success = await handleSubmit(values);
      if (success) {
        form.resetFields();
      }
    } catch (error) {
      // 表单验证失败
    }
  };

  // 处理抽屉关闭
  const onDrawerClose = () => {
    setModalVisible(false);
    form.resetFields();
  };

  // 打开编辑时设置表单值
  useEffect(() => {
    if (modalVisible && editingBrand) {
      form.setFieldsValue(editingBrand);
    } else if (modalVisible) {
      form.resetFields();
    }
  }, [modalVisible, editingBrand, form]);

  // 计算统计数据
  const stats = useMemo(() => {
    const total = brands.length;
    const active = brands.filter((brand) => brand.is_active).length;
    const inactive = brands.filter((brand) => !brand.is_active).length;
    const withSKU = brands.filter((brand) => brand.sku_count > 0).length;
    return { total, active, inactive, withSKU };
  }, [brands]);

  const columns = createBrandTableColumns({
    onEdit: handleEdit,
    onDelete: handleDelete,
    onToggleActive: handleToggleActive,
  });

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <PageHeader
        title="品牌管理"
        subtitle="管理产品品牌信息，关联SKU和分析数据"
        breadcrumbs={[
          { title: '首页', href: '/' },
          { title: '品牌管理' },
        ]}
        actions={[
          {
            key: 'create',
            label: '新建品牌',
            type: 'primary',
            icon: <PlusOutlined />,
            onClick: handleAdd,
          },
        ]}
      />

      {/* 内容区域 - 可滚动 */}
      <div style={{
        flex: 1,
        overflow: 'auto',
        padding: '24px',
        background: '#f5f5f5'
      }}>
        {/* 统计卡片 */}
        <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} lg={6}>
            <StatsCard
              title="总品牌数"
              value={stats.total}
              icon={<TagsOutlined />}
              color="blue"
              loading={loading}
            />
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatsCard
              title="启用中"
              value={stats.active}
              icon={<CheckCircleOutlined />}
              color="green"
              loading={loading}
              trend={
                stats.active > 0
                  ? {
                    value: Math.round((stats.active / stats.total) * 100),
                    type: 'increase',
                    label: '占比',
                    suffix: '%',
                  }
                  : undefined
              }
            />
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatsCard
              title="已停用"
              value={stats.inactive}
              icon={<StopOutlined />}
              color="red"
              loading={loading}
            />
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatsCard
              title="关联SKU"
              value={stats.withSKU}
              icon={<ShoppingOutlined />}
              color="purple"
              loading={loading}
            />
          </Col>
        </Row>

        {/* 表格或空状态 */}
        {brands.length === 0 && !loading ? (
          <EmptyState
            type="no-data"
            title="还没有品牌"
            description="创建第一个品牌开始管理您的产品"
            action={{
              label: '新建品牌',
              onClick: handleAdd,
            }}
          />
        ) : (
          <Table
            columns={columns}
            dataSource={brands}
            loading={loading}
            rowKey="id"
            scroll={{ x: 1300 }}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `共 ${total} 条`,
            }}
          />
        )}

        <Drawer
          title={editingBrand ? '编辑品牌' : '新建品牌'}
          open={modalVisible}
          onClose={onDrawerClose}
          width={720}
          placement="right"
          footer={
            <Space style={{ float: 'right' }}>
              <Button onClick={onDrawerClose}>
                取消
              </Button>
              <Button type="primary" onClick={onDrawerSubmit}>
                {editingBrand ? '保存' : '创建'}
              </Button>
            </Space>
          }
        >
          <BrandForm form={form} editingBrand={editingBrand} />
        </Drawer>
      </div>
    </div>
  );
};

export default BrandManagement;

